import { useMemo } from "react";
import { Box, Chip, Collapse, Stack, Typography } from "@mui/material";
import { FILTER_CATEGORIES } from "../constants/filterCategories";
import {
  itemMatchesFilters,
  matchesSearch,
} from "../utils/initiativeFilters";
import { useLookups } from "../hooks/useLookups";
import { getValueComparators } from "../utils/valueComparators";
import DateRangeFilterRow from "./DateRangeFilterRow";

export default function FilterPanel({
  isOpen,
  items,
  filters,
  searchQuery,
  onToggle,
  categories: customCategories,
  matchItem: customMatchItem,
  itemLabel,
  dateRange,
  onDateRangeChange,
}) {
  const lookups = useLookups();
  const valueComparators = getValueComparators(lookups);

  const categories = customCategories ?? FILTER_CATEGORIES;
  const matchItemFn = customMatchItem ?? itemMatchesFilters;
  const { singular = "initiative", plural = "initiatives" } = itemLabel ?? {};

  const visibleCategories = useMemo(
    () =>
      categories.reduce((visible, { key, label, getValues }) => {
        const rawValues = [...new Set(items.flatMap(getValues))].filter(
          Boolean,
        );
        const compare = valueComparators?.[key];
        const options = compare ? [...rawValues].sort(compare) : [...rawValues].sort();
        if (options.length > 0) visible.push({ key, label, options });
        return visible;
      }, []),
    [items, categories, valueComparators],
  );
  const matchCount = useMemo(
    () =>
      items.filter(
        (item) =>
          matchItemFn(item, filters) &&
          matchesSearch(item, searchQuery),
      ).length,
    [items, filters, searchQuery, matchItemFn],
  );

  const styles = {
    panel: {
      bgcolor: "background.tableHeader",
      borderBottom: 1,
      borderColor: "divider",
    },
    body: { px: 2, pt: 1, pb: 2 },
    categoryRow: { py: 0.75 },
    categoryLabel: {
      flex: "0 0 130px",
      pt: 0.5,
      fontWeight: 700,
      fontSize: "0.7rem",
      color: "background.tableHeaderText",
      textTransform: "uppercase",
      letterSpacing: "0.07em",
    },
    optionsRow: { flexWrap: "wrap", gap: 0.75 },
    neutral: {
      borderColor: "background.tableHeaderText",
      color: "background.tableHeaderText",
    },
    matchCount: {
      pb: 1,
      fontSize: "0.75rem",
      fontWeight: 600,
      color: "background.tableHeaderText",
    },
  };

  return (
    <Collapse in={isOpen}>
      <Box sx={styles.panel}>
        <Box sx={styles.body}>
          <Typography sx={styles.matchCount}>
            Showing {matchCount} {matchCount === 1 ? singular : plural}
          </Typography>
          {dateRange !== undefined && (
            <DateRangeFilterRow
              dateRange={dateRange}
              onChange={onDateRangeChange}
            />
          )}
          {visibleCategories.map(({ key, label, options }) => (
            <Stack
              key={key}
              direction="row"
              alignItems="flex-start"
              spacing={1.5}
              sx={styles.categoryRow}
            >
              <Typography sx={styles.categoryLabel}>{label}</Typography>
              <Stack direction="row" sx={styles.optionsRow}>
                {options.map((value) => (
                  <Chip
                    key={value}
                    label={value}
                    size="small"
                    onClick={() => onToggle(key, value)}
                    color={
                      filters[key]?.[value] === "exclude"
                        ? "error"
                        : filters[key]?.[value] === "include"
                          ? "primary"
                          : undefined
                    }
                    variant={filters[key]?.[value] ? "filled" : "outlined"}
                    sx={filters[key]?.[value] ? undefined : styles.neutral}
                  />
                ))}
              </Stack>
            </Stack>
          ))}
        </Box>
      </Box>
    </Collapse>
  );
}
